import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus, Plus, Trash2, GripVertical } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { registerContestsData } from "@/Api/contests";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { getSelectProductGroups } from "@/Api/productGroups";
import { getSelectProducts } from "@/Api/products";
import { getShowContests } from "@/Api/contests";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { Mask } from "@/utils/mask";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const step1Schema = z.object({
  code: z.string({ message: "*requerido" }).min(1, "*requerido"),
  validation_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  lottery_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  duplicate_from: z.string().optional(),
  prize: z.string().min(1, "*requerido"),
});

const step2Schema = z.object({
  product_classifications: z
    .array(z.string())
    .min(1, "*Selecione pelo menos um bolão"),
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
};

const schema = z.object({
  ...step1Schema.shape,
  ...step2Schema.shape,
});

type SchemaType = z.infer<typeof schema>;

export const AddBtn = ({ updateFront, dataApi }) => {
  const [lotteries, setLotteries] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const stepControls = useStepControl();
  const [isOpen, setIsOpen] = useState(false);
  const [addAmount, setAddAmount] = useState("1");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
      validation_date: "",
      lottery_id: "",
      product_classifications: [],
      duplicate_from: "",
      prize: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      const lotteriesResponse = await getSelectProductGroups();
      setLotteries(lotteriesResponse.data?.response || []);
    };
    fetchData();
  }, [isOpen]);

  useEffect(() => {
    const lotteryId = form.watch("lottery_id");
    if (!lotteryId) {
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      const productsResponse = await getSelectProducts({
        lottery_id: lotteryId,
      });
      setProducts(productsResponse.data?.response || []);

      const duplicateFrom = form.watch("duplicate_from");
      if (!duplicateFrom) {
        form.setValue("product_classifications", []);
      }
    };
    fetchProducts();
  }, [form.watch("lottery_id")]);

  useEffect(() => {
    const duplicateFrom = form.watch("duplicate_from");
    if (duplicateFrom && duplicateFrom !== "none") {
      const fetchContest = async () => {
        const contestResponse = await getShowContests(duplicateFrom);
        const contest = contestResponse.data?.response;

        if (contest) {
          form.setValue("lottery_id", contest.lottery_id?.toString() || "");

          const productClassifications =
            contest.product_classifications?.map((pc: any) =>
              pc.product_classification_id.toString()
            ) || [];

          form.setValue("product_classifications", productClassifications);
          form.setValue("prize", Mask.formatCurrency(contest.prize || 0));
        }
      };
      fetchContest();
    } else if (duplicateFrom === "none") {
      form.setValue("lottery_id", "");
      form.setValue("product_classifications", []);
      form.setValue("prize", "");
    }
  }, [form.watch("duplicate_from")]);

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<SchemaType | null>(
    null
  );

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);
      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const addProducts = () => {
    const amount = parseInt(addAmount) || 1;
    if (amount <= 0) return;

    const currentProducts = form.getValues("product_classifications");
    const newProducts = Array(amount).fill("");
    form.setValue("product_classifications", [
      ...currentProducts,
      ...newProducts,
    ]);
  };

  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("product_classifications");
    currentProducts.splice(index, 1);
    form.setValue("product_classifications", currentProducts);
  };

  const handleDragStart = (start: any) => {
    setDraggedIndex(start.source.index);
  };

  const handleDragEnd = (result: any) => {
    setDraggedIndex(null);

    if (!result.destination) return;

    const items = Array.from(form.getValues("product_classifications"));
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    form.setValue("product_classifications", items, { shouldValidate: false });
  };

  const handleFormSubmit = (data: SchemaType) => {
    setPendingFormData(data);
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    if (!pendingFormData) return;

    setFormState("loading");

    const payload = {
      ...pendingFormData,
      name: `${pendingFormData.code.toString()}`,
      product_classifications: pendingFormData.product_classifications.map(
        (id) => parseInt(id)
      ),
      lottery_id: parseInt(pendingFormData.lottery_id),
      prize: pendingFormData.prize
        ? parseFloat(Mask.transformCurrencyInFloat(pendingFormData.prize))
        : null,
    };

    const { data: apiResponse, ok } = await registerContestsData(payload);
    if (!ok) {
      setFormState("error");
      setShowConfirmModal(false);
      return;
    }

    updateFront((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev?.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    // Fecha ambos os modais e reseta o formulário apenas após sucesso
    setShowConfirmModal(false);
    setIsOpen(false);
    stepControls.setStep(1);
    setFormState("default");
    form.reset();
    setPendingFormData(null);

    toast({
      description: `Concurso criado com sucesso!`,
      action: <Check color="#1edc2b" />,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !showConfirmModal) {
      stepControls.setStep(1);
      form.reset();
      setIsOpen(open);
    } else if (!open && showConfirmModal) {
      // Impede o fechamento do modal principal quando o modal de confirmação está aberto
      return;
    } else {
      setIsOpen(open);
    }
  };

  const getSelectedProductsDetails = () => {
    const selectedIds = form.getValues("product_classifications");

    const productCounts = selectedIds.reduce((acc, id) => {
      acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    let totalGames = 0;
    let totalQuotas = 0;
    let totalValue = 0;

    const productDetails = Object.entries(productCounts).map(([id, count]) => {
      const product = products.find((p) => p.id.toString() === id);

      if (product) {
        totalGames += (product.game_quantity || 0) * count;
        totalQuotas += (product.quota_quantity || 0) * count;

        const match = product.name.match(/R\$ (\d+[.,]\d+)/);
        const unitValue = match ? parseFloat(match[1].replace(",", ".")) : 0;
        totalValue += unitValue * count;
      }

      return {
        name: product?.name || "Sem Produto",
        count,
      };
    });

    return { totalGames, totalQuotas, totalValue, productDetails };
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className={cn("py-3 flex gap-1", `bg-primary`)}
          >
            <CirclePlus className="size-5" strokeWidth={1.75} />
            Adicionar Concurso
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[650px] h-auto">
          <DialogHeader className="flex flex-col gap-2">
            <DialogDescription className="text-lg">
              Adicionar Concurso
            </DialogDescription>
          </DialogHeader>

          <FormSteps controls={stepControls}>
            <FormSteps.Header>
              <FormSteps.Indicator step={1}>
                Informações Básicas
              </FormSteps.Indicator>
              <FormSteps.Indicator step={2}>Bolões</FormSteps.Indicator>
            </FormSteps.Header>

            <ScrollArea className="h-[calc(90vh-10rem)] pr-4">
              <Form
                onSubmit={form.handleSubmit(handleFormSubmit)}
                {...form}
                className="space-y-3 p-2"
              >
                <FormSteps.Content step={1}>
                  <Form.Row>
                    <Form.Field name="duplicate_from">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Duplicar de Concurso Existente
                          </Form.Label>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                            }}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione um concurso para duplicar (opcional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {dataApi?.data.map((contest) => (
                                <SelectItem
                                  key={contest.uuid}
                                  value={contest.uuid}
                                >
                                  {contest.name} - {contest.lottery.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="lottery_id">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Loteria <Form.Message />
                          </Form.Label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione uma loteria" />
                            </SelectTrigger>
                            <SelectContent>
                              {lotteries.map((lottery) => (
                                <SelectItem
                                  key={lottery.id}
                                  value={lottery.id.toString()}
                                >
                                  {lottery.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="code">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Concurso <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              type="number"
                              placeholder="Ex: 2455"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="validation_date">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Data de Validação <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input type="date" {...field} />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <Form.Row>
                    <Form.Field name="prize">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Valor do Prêmio <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              placeholder="R$ 0,00"
                              typeMask="currency"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <FormSteps.Content step={2}>
                  <Form.Row>
                    <Form.Field name="product_classifications">
                      {({ field }) => (
                        <Form.Item className="w-full">
                          <Form.Label className="flex justify-between items-center">
                            <span>
                              Bolões <Form.Message />
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {field.value.length > 0 &&
                                `Bolões - ${field.value.length}`}
                            </span>
                          </Form.Label>
                          <div className="flex flex-col gap-4">
                            <DragDropContext
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                            >
                              <Droppable droppableId="products" type="product">
                                {(provided, snapshot) => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={cn(
                                      "space-y-2 min-h-[2rem] transition-all duration-200",
                                      snapshot.isDraggingOver &&
                                        "bg-accent/30 rounded-lg"
                                    )}
                                  >
                                    {field.value.map((_, index) => (
                                      <Draggable
                                        key={`product-${index}`}
                                        draggableId={`product-${index}`}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className={cn(
                                              "flex items-center gap-2 p-3 bg-background border rounded-lg transition-all duration-200",
                                              snapshot.isDragging &&
                                                "shadow-lg ring-2 ring-primary/20 bg-accent/50 scale-[1.02] z-50",
                                              draggedIndex === index &&
                                                !snapshot.isDragging &&
                                                "transform-none opacity-100"
                                            )}
                                            style={{
                                              ...provided.draggableProps.style,
                                              ...(snapshot.isDragging && {
                                                transform:
                                                  provided.draggableProps.style
                                                    ?.transform || "none",
                                              }),
                                            }}
                                          >
                                            <div
                                              {...provided.dragHandleProps}
                                              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground transition-colors cursor-grab active:cursor-grabbing rounded hover:bg-accent/50"
                                            >
                                              <GripVertical className="h-4 w-4" />
                                            </div>

                                            <div className="flex-shrink-0 text-sm font-medium text-muted-foreground min-w-[2rem]">
                                              B{index + 1}
                                            </div>

                                            <Select
                                              value={field.value[index]}
                                              onValueChange={(value) => {
                                                const newValues = [
                                                  ...field.value,
                                                ];
                                                newValues[index] = value;
                                                field.onChange(newValues);
                                              }}
                                            >
                                              <SelectTrigger className="flex-1">
                                                <SelectValue placeholder="Selecione um bolão" />
                                              </SelectTrigger>
                                              <SelectContent
                                                searchable
                                                searchPlaceholder="Filtrar produtos..."
                                              >
                                                {products.map((product) => (
                                                  <SelectItem
                                                    key={product.id}
                                                    value={product.id.toString()}
                                                  >
                                                    {product.name}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>

                                            <Button
                                              type="button"
                                              size="icon"
                                              variant="ghost"
                                              onClick={() =>
                                                removeProduct(index)
                                              }
                                              className="flex-shrink-0 h-8 w-8 text-destructive hover:text-destructive-foreground hover:bg-destructive/90 transition-colors"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>

                            <div className="flex gap-2">
                              <Input
                                type="number"
                                value={addAmount}
                                onChange={(e) => setAddAmount(e.target.value)}
                                min="1"
                                className="w-24"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addProducts}
                                className="flex-1"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Adicionar Bolões
                              </Button>
                            </div>

                            {field.value.length > 0 && (
                              <div className="mt-4 p-4 bg-muted rounded-lg">
                                <h4 className="font-medium mb-2">Resumo</h4>
                                <div className="space-y-1 text-sm">
                                  {(() => {
                                    const {
                                      totalGames,
                                      totalQuotas,
                                      totalValue,
                                      productDetails,
                                    } = getSelectedProductsDetails();
                                    return (
                                      <>
                                        <p>Total de Jogos: {totalGames}</p>
                                        <p>Total de Cotas: {totalQuotas}</p>
                                        <p>
                                          Valor Total:{" "}
                                          {Mask.formatCurrency(totalValue)}
                                        </p>
                                        <div className="mt-2">
                                          <p className="font-medium">
                                            Quantidade por bolão:
                                          </p>
                                          {productDetails.map(
                                            (product, index) => (
                                              <p key={index}>
                                                {product.count}x: {product.name}
                                              </p>
                                            )
                                          )}
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <DialogFooter className="sticky bottom-0 pt-4">
                  <div className="flex w-full gap-2">
                    {stepControls.step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          stepControls.setStep(stepControls.step - 1)
                        }
                      >
                        Voltar
                      </Button>
                    )}

                    {stepControls.step < 2 && (
                      <Button
                        type="button"
                        className={cn("flex-1", colors.bg_primary)}
                        onClick={handleNextStep}
                      >
                        Próximo
                      </Button>
                    )}

                    {stepControls.step === 2 && (
                      <Button
                        type="submit"
                        className={cn("flex-1", colors.bg_primary)}
                        disabled={formState !== "default"}
                      >
                        {formState === "loading" && (
                          <>
                            Adicionando <LoaderSpin />
                          </>
                        )}
                        {formState === "default" && "Adicionar"}
                        {formState === "error" && "Erro ao adicionar"}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </Form>
            </ScrollArea>
          </FormSteps>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showConfirmModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowConfirmModal(false);
          }
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Criação do Concurso</AlertDialogTitle>
            <AlertDialogDescription>
              Revise as informações do concurso antes de confirmar a criação.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pendingFormData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">
                    Concurso
                  </h4>
                  <p className="font-semibold">{pendingFormData.code}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">
                    Loteria
                  </h4>
                  <p
                    className="font-semibold"
                    style={{
                      color: lotteries.find(
                        (l) => l.id.toString() === pendingFormData.lottery_id
                      )?.color || undefined
                    }}
                  >
                    {lotteries.find(
                      (l) => l.id.toString() === pendingFormData.lottery_id
                    )?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">
                    Data de Validação
                  </h4>
                  <p className="font-semibold">
                    {Mask.date(pendingFormData.validation_date)}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">
                    Valor do Prêmio
                  </h4>
                  <p className="font-semibold">
                    {pendingFormData.prize || "N/A"}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium">
                  Bolões Selecionados (
                  {pendingFormData.product_classifications.length})
                </h4>
                <ScrollArea className="h-60 w-full">
                  <div className="space-y-2 pr-4">
                    {pendingFormData.product_classifications.map(
                      (productId, index) => {
                        const product = products.find(
                          (p) => p.id.toString() === productId
                        );
                        return (
                          <div
                            key={`${productId}-${index}`}
                            className="flex justify-between items-center p-3 border rounded-lg bg-background"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center text-sm font-medium">
                                B{index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {product?.name || "Produto não encontrado"}
                                </p>
                                <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                                  <span>
                                    Jogos: {product?.game_quantity || 0}
                                  </span>
                                  <span>
                                    Cotas: {product?.quota_quantity || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </ScrollArea>

                {(() => {
                  const { totalGames, totalQuotas, totalValue } =
                    getSelectedProductsDetails();
                  return (
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <h5 className="font-medium mb-2 text-primary">
                        Resumo Geral
                      </h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            Total de Jogos:
                          </span>
                          <p className="font-semibold">{totalGames}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Total de Cotas:
                          </span>
                          <p className="font-semibold">{totalQuotas}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Valor Total:
                          </span>
                          <p className="font-semibold">
                            {Mask.formatCurrency(totalValue)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSubmit}
              className={cn(colors.bg_primary)}
              disabled={formState === "loading"}
            >
              {formState === "loading" ? (
                <>
                  Criando... <LoaderSpin />
                </>
              ) : (
                "Confirmar e Criar Concurso"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
